import { Link } from "react-router-dom";
import { useCompactRun, useDeleteRun, useRuns } from "../api/queries";
import { Card, ErrorNote, Loading, PageHeader } from "../components/ui";
import type { RunSummary } from "../api/types";
import { fmtBytes } from "../lib/format";

function fmtAge(mtime: number): string {
  const s = Date.now() / 1000 - mtime;
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
}

function RunCard({ r }: { r: RunSummary }) {
  const del = useDeleteRun();
  const compact = useCompactRun();
  const running = r.status === "running";
  return (
    <div className="run-card">
      <Link to={`/runs/${encodeURIComponent(r.name)}`} className="run-card-main">
        <div className="run-card-top">
          <span className="run-name">{r.name}</span>
          <span className={`chip chip-${r.status}`}>{r.adopted ? "adopted" : r.status}</span>
        </div>
        <div className="run-card-meta dim mono">
          {r.objective && <span>{r.objective}</span>}
          {r.best_fitness != null && <span>fit {r.best_fitness.toFixed(4)}</span>}
          {r.generation != null && <span>gen {r.generation}</span>}
          <span>{fmtBytes(r.size_bytes)}</span>
          <span>{fmtAge(r.mtime)} ago</span>
        </div>
      </Link>
      {!running && (
        <div className="run-card-actions">
          <button onClick={() => { if (confirm(`Compact ${r.name}? Drops the per-gen population genomes (keeps best + curves).`)) compact.mutate(r.name); }}
            disabled={compact.isPending}>Compact</button>
          <button className="danger" onClick={() => { if (confirm(`Delete ${r.name}? This removes the run entirely.`)) del.mutate(r.name); }}
            disabled={del.isPending}>Delete</button>
        </div>
      )}
    </div>
  );
}

export function Runs() {
  const { data, isLoading, error } = useRuns();
  const total = (data ?? []).reduce((s, r) => s + r.size_bytes, 0);
  return (
    <div className="page">
      <PageHeader title="Runs" sub={data ? `${data.length} · ${fmtBytes(total)}` : undefined} />
      {isLoading && <Loading what="runs" />}
      {error && <ErrorNote error={error} />}
      {data && data.length === 0 && (
        <Card><p className="dim">No runs yet. Launch via Claude Code.</p></Card>
      )}
      {data && data.length > 0 && (
        <div className="run-list">
          {data.map((r) => <RunCard key={r.name} r={r} />)}
        </div>
      )}
    </div>
  );
}
